import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import Navigation from "../components/layout/Navigation";
import Footer from "../components/layout/Footer";
import SalesLeaderboard from "../components/sales/SalesLeaderboard";
import SalesToolkit from "../components/sales/SalesToolkit";
import PerformanceGoals from "../components/sales/PerformanceGoals";
import { toast } from "sonner";
import { 
  QrCode, DollarSign, Users, TrendingUp, Building2, 
  Phone, Mail, MapPin, Plus, Search, Filter, Edit, Trash2,
  CheckCircle, Clock, AlertCircle, ArrowUpRight, Target,
  Briefcase, Award, Copy, ExternalLink, Calendar, MessageSquare,
  UserPlus, ChevronRight, Sparkles, Eye, FileText, Trophy, Wrench
} from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { API } from "../apiConfig";


const SalesAgentDashboardPage = () => {
  const { user, token } = useAuth();
  const [dashboard, setDashboard] = useState({
    agent: null,
    stats: { total_sales: 0, total_commission: 0, pending_commission: 0, linked_accounts: 0, total_leads: 0, leads_by_status: {}, conversion_rate: 0 },
    linked_accounts: [],
    leads: [],
    recent_commissions: [],
    communication_logs: [],
    reminders: [],
    goals: []
  });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [showNewLeadDialog, setShowNewLeadDialog] = useState(false);
  const [showQRDialog, setShowQRDialog] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [leadFilter, setLeadFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API}/sales-agent/dashboard`, { headers });
      setDashboard(response.data);
    } catch (error) {
      console.error("Error fetching dashboard:", error);
      toast.error("Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD'
    }).format(price);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      cold: "bg-blue-100 text-blue-700",
      contacted: "bg-yellow-100 text-yellow-700",
      warm: "bg-orange-100 text-orange-700",
      hot: "bg-red-100 text-red-700",
      converted: "bg-green-100 text-green-700",
      lost: "bg-gray-100 text-gray-700"
    };
    return colors[status] || "bg-gray-100 text-gray-700";
  };

  const registrationUrl = dashboard?.qr_registration_url 
    ? `${window.location.origin}${dashboard.qr_registration_url}`
    : "";

  const copyRegistrationLink = () => {
    navigator.clipboard.writeText(registrationUrl);
    toast.success("Registration link copied!");
  };

  // Filter leads
  const filteredLeads = dashboard?.leads?.filter(lead => {
    const matchesFilter = leadFilter === "all" || lead.status === leadFilter;
    const matchesSearch = !searchTerm || 
      lead.business_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.contact_name?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  }) || [];

  if (loading) {
    return (
      <div className="min-h-screen">
        <Navigation />
        <div className="pt-32 pb-24 px-6 md:px-12">
          <div className="max-w-7xl mx-auto">
            <div className="animate-pulse space-y-6">
              <div className="h-10 bg-muted rounded w-1/3" />
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                {[1, 2, 3, 4].map(i => (
                  <div key={i} className="h-32 bg-muted rounded-2xl" />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const agent = dashboard?.agent;
  const tier = dashboard?.tier;

  return (
    <div className="min-h-screen bg-background" data-testid="sales-agent-dashboard">
      <Navigation />

      <section className="pt-28 pb-24 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h1 className="text-3xl md:text-4xl font-medium mb-2">
                  Sales Agent Dashboard
                </h1>
                <p className="text-muted-foreground">
                  Welcome back, {user?.name}! Agent Code: <span className="font-mono font-medium text-primary">{agent?.agent_code}</span>
                </p>
              </div>
              <div className="flex items-center gap-3">
                <Badge className={`px-4 py-2 ${tier?.name === "Elite" ? "bg-gradient-to-r from-amber-500 to-yellow-500" : tier?.name === "Pro" ? "bg-primary" : "bg-muted text-foreground"}`}>
                  <Award className="h-4 w-4 mr-1" />
                  {tier?.name || "Starter"} Agent
                </Badge>
                <Button onClick={() => setShowQRDialog(true)} className="rounded-full">
                  <QrCode className="h-4 w-4 mr-2" />
                  Show QR Code
                </Button>
              </div>
            </div>
          </motion.div>

          {/* Stats Cards */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-8"
          >
            <Card className="rounded-2xl border-border">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-primary/10 rounded-xl">
                    <Building2 className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{agent?.total_accounts || 0}</p>
                    <p className="text-sm text-muted-foreground">Linked Accounts</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="rounded-2xl border-border">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-green-100 rounded-xl">
                    <DollarSign className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{formatPrice(agent?.total_sales || 0)}</p>
                    <p className="text-sm text-muted-foreground">Total Sales</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="rounded-2xl border-border">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-amber-100 rounded-xl">
                    <TrendingUp className="h-6 w-6 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{formatPrice(dashboard?.total_pending_commission || 0)}</p>
                    <p className="text-sm text-muted-foreground">Pending Commission</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="rounded-2xl border-border">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-blue-100 rounded-xl">
                    <Target className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{dashboard?.lead_stats?.total || 0}</p>
                    <p className="text-sm text-muted-foreground">Active Leads</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Lead Pipeline Overview */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mb-8"
          >
            <Card className="rounded-2xl">
              <CardHeader>
                <CardTitle className="text-lg">Lead Pipeline</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2">
                  {[
                    { status: "cold", label: "Cold", count: dashboard?.lead_stats?.cold || 0, color: "bg-blue-500" },
                    { status: "warm", label: "Warm", count: dashboard?.lead_stats?.warm || 0, color: "bg-orange-500" },
                    { status: "hot", label: "Hot", count: dashboard?.lead_stats?.hot || 0, color: "bg-red-500" },
                    { status: "converted", label: "Converted", count: dashboard?.lead_stats?.converted || 0, color: "bg-green-500" },
                  ].map((stage, idx) => (
                    <div key={stage.status} className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <div className={`w-3 h-3 rounded-full ${stage.color}`} />
                        <span className="text-sm font-medium">{stage.label}</span>
                        <span className="text-sm text-muted-foreground">({stage.count})</span>
                      </div>
                      <div className="h-2 bg-muted rounded-full overflow-hidden">
                        <div 
                          className={`h-full ${stage.color} rounded-full transition-all`}
                          style={{ 
                            width: dashboard?.lead_stats?.total > 0 
                              ? `${(stage.count / dashboard.lead_stats.total) * 100}%` 
                              : '0%' 
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className="grid grid-cols-7 w-full max-w-3xl bg-muted rounded-xl p-1">
              <TabsTrigger value="overview" className="rounded-lg">Overview</TabsTrigger>
              <TabsTrigger value="leads" className="rounded-lg">Leads</TabsTrigger>
              <TabsTrigger value="accounts" className="rounded-lg">Accounts</TabsTrigger>
              <TabsTrigger value="commissions" className="rounded-lg">Commissions</TabsTrigger>
              <TabsTrigger value="toolkit" className="rounded-lg gap-1">
                <Wrench className="w-3 h-3" /> Toolkit
              </TabsTrigger>
              <TabsTrigger value="goals" className="rounded-lg gap-1">
                <Trophy className="w-3 h-3" /> Goals
              </TabsTrigger>
              <TabsTrigger value="leaderboard" className="rounded-lg gap-1">
                <Award className="w-3 h-3" /> Rank
              </TabsTrigger>
            </TabsList>

            {/* Overview Tab */}
            <TabsContent value="overview" className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* QR Code Card */}
                <Card className="rounded-2xl lg:col-span-1">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <QrCode className="h-5 w-5 text-primary" />
                      Your Registration QR
                    </CardTitle>
                    <CardDescription>
                      Show this to businesses to register them as wholesale accounts linked to you
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="flex flex-col items-center">
                    <div className="p-4 bg-white rounded-2xl shadow-lg mb-4">
                      <QRCodeSVG 
                        value={registrationUrl}
                        size={180}
                        level="H"
                        includeMargin={true}
                      />
                    </div>
                    <p className="text-xs text-muted-foreground text-center mb-4">
                      Agent Code: <span className="font-mono font-medium">{agent?.agent_code}</span>
                    </p>
                    <div className="flex gap-2 w-full">
                      <Button variant="outline" className="flex-1 rounded-full" onClick={copyRegistrationLink}>
                        <Copy className="h-4 w-4 mr-2" />
                        Copy Link
                      </Button>
                      <Button variant="outline" className="rounded-full" onClick={() => setShowQRDialog(true)}>
                        <ExternalLink className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Tier Progress */}
                <Card className="rounded-2xl lg:col-span-2">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Sparkles className="h-5 w-5 text-primary" />
                      Agent Tier Progress
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-6">
                      {/* Current Tier */}
                      <div className="flex items-center justify-between p-4 bg-primary/5 rounded-xl border border-primary/20">
                        <div>
                          <p className="font-medium">Current Tier: {tier?.name}</p>
                          <p className="text-sm text-muted-foreground">
                            Base Commission: {((tier?.base_commission || 0.05) * 100).toFixed(0)}%
                          </p>
                        </div>
                        <Badge className="bg-primary">{agent?.total_accounts || 0} Accounts</Badge>
                      </div>

                      {/* Tier Ladder */}
                      <div className="space-y-3">
                        {[
                          { name: "Starter", min: 0, max: 10, commission: "5%" },
                          { name: "Pro", min: 10, max: 25, commission: "7%" },
                          { name: "Elite", min: 25, max: null, commission: "10%" }
                        ].map((t, idx) => {
                          const isCurrentTier = tier?.name === t.name;
                          const isPastTier = (agent?.total_accounts || 0) >= (t.max || Infinity);
                          
                          return (
                            <div 
                              key={t.name}
                              className={`flex items-center justify-between p-3 rounded-xl border transition-all ${
                                isCurrentTier 
                                  ? "bg-primary/10 border-primary" 
                                  : isPastTier 
                                    ? "bg-green-50 border-green-200"
                                    : "bg-muted/30 border-border"
                              }`}
                            >
                              <div className="flex items-center gap-3">
                                {isPastTier || isCurrentTier ? (
                                  <CheckCircle className={`h-5 w-5 ${isCurrentTier ? "text-primary" : "text-green-500"}`} />
                                ) : (
                                  <div className="w-5 h-5 rounded-full border-2 border-muted-foreground/30" />
                                )}
                                <div>
                                  <p className="font-medium">{t.name}</p>
                                  <p className="text-xs text-muted-foreground">
                                    {t.min}-{t.max || "∞"} accounts
                                  </p>
                                </div>
                              </div>
                              <Badge variant={isCurrentTier ? "default" : "outline"}>
                                {t.commission} commission
                              </Badge>
                            </div>
                          );
                        })}
                      </div>

                      {/* Progress to Next Tier */}
                      {tier?.name !== "Elite" && (
                        <div className="pt-4 border-t">
                          <div className="flex justify-between text-sm mb-2">
                            <span>Progress to {tier?.name === "Starter" ? "Pro" : "Elite"}</span>
                            <span className="font-medium">
                              {agent?.total_accounts || 0} / {tier?.name === "Starter" ? 10 : 25} accounts
                            </span>
                          </div>
                          <div className="h-3 bg-muted rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-gradient-to-r from-primary to-primary/80 rounded-full transition-all"
                              style={{ 
                                width: `${Math.min(100, ((agent?.total_accounts || 0) / (tier?.name === "Starter" ? 10 : 25)) * 100)}%` 
                              }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Recent Activity */}
              <Card className="rounded-2xl">
                <CardHeader>
                  <CardTitle className="text-lg">Recent Leads</CardTitle>
                </CardHeader>
                <CardContent>
                  {filteredLeads.length === 0 ? (
                    <div className="text-center py-8">
                      <Target className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
                      <p className="text-muted-foreground">No leads yet. Start adding prospects!</p>
                      <Button 
                        className="mt-4 rounded-full" 
                        onClick={() => setShowNewLeadDialog(true)}
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Add First Lead
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {filteredLeads.slice(0, 5).map((lead) => (
                        <div 
                          key={lead.lead_id}
                          className="flex items-center justify-between p-4 bg-muted/30 rounded-xl hover:bg-muted/50 transition-colors cursor-pointer"
                          onClick={() => setSelectedLead(lead)}
                        >
                          <div className="flex items-center gap-4">
                            <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                              <Building2 className="h-5 w-5 text-primary" />
                            </div>
                            <div>
                              <p className="font-medium">{lead.business_name}</p>
                              <p className="text-sm text-muted-foreground">{lead.contact_name}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <Badge className={getStatusColor(lead.status)}>
                              {lead.status}
                            </Badge>
                            <ChevronRight className="h-4 w-4 text-muted-foreground" />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Leads Tab */}
            <TabsContent value="leads" className="space-y-6">
              {/* Lead Management Header */}
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input 
                      placeholder="Search leads..."
                      className="pl-10 w-64 rounded-xl"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                  <Select value={leadFilter} onValueChange={setLeadFilter}>
                    <SelectTrigger className="w-40 rounded-xl">
                      <Filter className="h-4 w-4 mr-2" />
                      <SelectValue placeholder="Filter" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Leads</SelectItem>
                      <SelectItem value="cold">Cold</SelectItem>
                      <SelectItem value="contacted">Contacted</SelectItem>
                      <SelectItem value="warm">Warm</SelectItem>
                      <SelectItem value="hot">Hot</SelectItem>
                      <SelectItem value="converted">Converted</SelectItem>
                      <SelectItem value="lost">Lost</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button className="rounded-full" onClick={() => setShowNewLeadDialog(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add New Lead
                </Button>
              </div>

              {/* Leads Table */}
              <Card className="rounded-2xl">
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Business</TableHead>
                        <TableHead>Contact</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Source</TableHead>
                        <TableHead>Follow Up</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredLeads.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                            No leads found
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredLeads.map((lead) => (
                          <TableRow key={lead.lead_id} className="cursor-pointer hover:bg-muted/50">
                            <TableCell>
                              <div className="font-medium">{lead.business_name}</div>
                              {lead.address && (
                                <div className="text-xs text-muted-foreground">{lead.address}</div>
                              )}
                            </TableCell>
                            <TableCell>
                              <div>{lead.contact_name}</div>
                              {lead.phone && (
                                <div className="text-xs text-muted-foreground">{lead.phone}</div>
                              )}
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline">{lead.business_type || "Unknown"}</Badge>
                            </TableCell>
                            <TableCell>
                              <Badge className={getStatusColor(lead.status)}>{lead.status}</Badge>
                            </TableCell>
                            <TableCell className="capitalize">{lead.source?.replace("_", " ")}</TableCell>
                            <TableCell>
                              {lead.next_follow_up ? (
                                <div className="flex items-center gap-1 text-sm">
                                  <Calendar className="h-3 w-3" />
                                  {formatDate(lead.next_follow_up)}
                                </div>
                              ) : (
                                <span className="text-muted-foreground">-</span>
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-2">
                                <Button 
                                  variant="ghost" 
                                  size="icon"
                                  onClick={() => setSelectedLead(lead)}
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Accounts Tab */}
            <TabsContent value="accounts" className="space-y-6">
              <Card className="rounded-2xl">
                <CardHeader>
                  <CardTitle>Linked Wholesale Accounts</CardTitle>
                  <CardDescription>
                    Businesses registered through your QR code
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {dashboard?.linked_accounts?.length === 0 ? (
                    <div className="text-center py-12">
                      <UserPlus className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
                      <p className="text-muted-foreground mb-2">No linked accounts yet</p>
                      <p className="text-sm text-muted-foreground mb-4">
                        Share your QR code with businesses to start building your network
                      </p>
                      <Button className="rounded-full" onClick={() => setShowQRDialog(true)}>
                        <QrCode className="h-4 w-4 mr-2" />
                        Show QR Code
                      </Button>
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Business</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Orders</TableHead>
                          <TableHead>Total Revenue</TableHead>
                          <TableHead>Your Commission</TableHead>
                          <TableHead>Linked Date</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(dashboard.linked_accounts || []).map((account) => (
                          <TableRow key={account.link_id}>
                            <TableCell>
                              <div className="font-medium">{account.business_name}</div>
                              {account.user_details?.email && (
                                <div className="text-xs text-muted-foreground">{account.user_details.email}</div>
                              )}
                            </TableCell>
                            <TableCell>
                              <Badge className={account.is_active ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}>
                                {account.is_active ? "Active" : "Pending"}
                              </Badge>
                            </TableCell>
                            <TableCell>{account.total_orders}</TableCell>
                            <TableCell>{formatPrice(account.total_revenue)}</TableCell>
                            <TableCell className="text-primary font-medium">
                              {formatPrice(account.total_commission)}
                            </TableCell>
                            <TableCell>{formatDate(account.linked_at)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Commissions Tab */}
            <TabsContent value="commissions" className="space-y-6">
              {/* Commission Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Card className="rounded-2xl">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <div className="p-3 bg-yellow-100 rounded-xl">
                        <Clock className="h-6 w-6 text-yellow-600" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold">{formatPrice(agent?.pending_commission || 0)}</p>
                        <p className="text-sm text-muted-foreground">Pending</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="rounded-2xl">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <div className="p-3 bg-green-100 rounded-xl">
                        <CheckCircle className="h-6 w-6 text-green-600" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold">{formatPrice(agent?.total_commission_paid || 0)}</p>
                        <p className="text-sm text-muted-foreground">Paid Out</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="rounded-2xl">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <div className="p-3 bg-primary/10 rounded-xl">
                        <TrendingUp className="h-6 w-6 text-primary" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold">{formatPrice(agent?.total_commission_earned || 0)}</p>
                        <p className="text-sm text-muted-foreground">Lifetime Earnings</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Commission History */}
              <Card className="rounded-2xl">
                <CardHeader>
                  <CardTitle>Commission History</CardTitle>
                </CardHeader>
                <CardContent>
                  {dashboard?.pending_commissions?.length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground">
                      <DollarSign className="h-12 w-12 mx-auto mb-4 opacity-30" />
                      <p>No commission records yet</p>
                      <p className="text-sm mt-2">Commissions will appear here when your linked accounts make purchases</p>
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Order</TableHead>
                          <TableHead>Category</TableHead>
                          <TableHead>Order Total</TableHead>
                          <TableHead>Rate</TableHead>
                          <TableHead>Commission</TableHead>
                          <TableHead>Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(dashboard.recent_commissions || []).map((comm) => (
                          <TableRow key={comm.commission_id}>
                            <TableCell>{formatDate(comm.created_at)}</TableCell>
                            <TableCell className="font-mono text-sm">{comm.order_id}</TableCell>
                            <TableCell className="capitalize">{comm.product_category}</TableCell>
                            <TableCell>{formatPrice(comm.order_total)}</TableCell>
                            <TableCell>{(comm.commission_rate * 100).toFixed(0)}%</TableCell>
                            <TableCell className="font-medium text-primary">
                              {formatPrice(comm.commission_amount)}
                            </TableCell>
                            <TableCell>
                              <Badge className={
                                comm.status === "paid" ? "bg-green-100 text-green-700" :
                                comm.status === "approved" ? "bg-blue-100 text-blue-700" :
                                "bg-yellow-100 text-yellow-700"
                              }>
                                {comm.status}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Toolkit Tab */}
            <TabsContent value="toolkit" className="space-y-6">
              <SalesToolkit token={token} />
            </TabsContent>

            {/* Performance Goals Tab */}
            <TabsContent value="goals" className="space-y-6">
              <PerformanceGoals token={token} />
            </TabsContent>

            {/* Leaderboard Tab */}
            <TabsContent value="leaderboard" className="space-y-6">
              <SalesLeaderboard token={token} />
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* New Lead Dialog */}
      <NewLeadDialog 
        open={showNewLeadDialog}
        onClose={() => setShowNewLeadDialog(false)}
        onSuccess={() => {
          setShowNewLeadDialog(false);
          fetchDashboard();
        }}
        headers={headers}
      />

      {/* Lead Detail Dialog */}
      <LeadDetailDialog
        lead={selectedLead}
        open={!!selectedLead}
        onClose={() => setSelectedLead(null)}
        onUpdate={() => {
          setSelectedLead(null);
          fetchDashboard();
        }}
        headers={headers}
      />

      {/* QR Code Dialog */}
      <Dialog open={showQRDialog} onOpenChange={setShowQRDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Your Wholesale Registration QR</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center py-6">
            <div className="p-6 bg-white rounded-2xl shadow-xl mb-6">
              <QRCodeSVG 
                value={registrationUrl}
                size={250}
                level="H"
                includeMargin={true}
              />
            </div>
            <p className="text-center text-muted-foreground mb-4">
              Show this QR code to businesses. When they scan and register, 
              they'll be automatically linked to your account.
            </p>
            <div className="flex items-center gap-2 p-3 bg-muted rounded-xl w-full">
              <span className="text-sm font-mono flex-1 truncate">{registrationUrl}</span>
              <Button variant="ghost" size="icon" onClick={copyRegistrationLink}>
                <Copy className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
};

// New Lead Dialog Component
const NewLeadDialog = ({ open, onClose, onSuccess, headers }) => {
  const [formData, setFormData] = useState({
    business_name: "",
    contact_name: "",
    email: "",
    phone: "",
    address: "",
    postcode: "",
    business_type: "",
    source: "cold_call",
    notes: ""
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      await axios.post(`${API}/sales-agent/leads`, formData, { headers });
      toast.success("Lead created successfully");
      setFormData({
        business_name: "",
        contact_name: "",
        email: "",
        phone: "",
        address: "",
        postcode: "",
        business_type: "",
        source: "cold_call",
        notes: ""
      });
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create lead");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Add New Lead</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="text-sm font-medium mb-1 block">Business Name *</label>
              <Input
                required
                value={formData.business_name}
                onChange={(e) => setFormData({ ...formData, business_name: e.target.value })}
                placeholder="e.g., Gallery XYZ"
                className="rounded-xl"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Contact Name *</label>
              <Input
                required
                value={formData.contact_name}
                onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                placeholder="e.g., John Smith"
                className="rounded-xl"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Phone</label>
              <Input
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="e.g., 0412 345 678"
                className="rounded-xl"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Email</label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="e.g., contact@gallery.com"
                className="rounded-xl"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Business Type</label>
              <Select 
                value={formData.business_type} 
                onValueChange={(v) => setFormData({ ...formData, business_type: v })}
              >
                <SelectTrigger className="rounded-xl">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="gallery">Art Gallery</SelectItem>
                  <SelectItem value="gift_shop">Gift Shop</SelectItem>
                  <SelectItem value="hotel">Hotel/Resort</SelectItem>
                  <SelectItem value="corporate">Corporate</SelectItem>
                  <SelectItem value="interior_design">Interior Design</SelectItem>
                  <SelectItem value="retail">Retail Store</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <label className="text-sm font-medium mb-1 block">Address</label>
              <Input
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="e.g., 123 Main St, Sydney"
                className="rounded-xl"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Postcode</label>
              <Input
                value={formData.postcode}
                onChange={(e) => setFormData({ ...formData, postcode: e.target.value })}
                placeholder="e.g., 2000"
                className="rounded-xl"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Lead Source</label>
              <Select 
                value={formData.source} 
                onValueChange={(v) => setFormData({ ...formData, source: v })}
              >
                <SelectTrigger className="rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cold_call">Cold Call</SelectItem>
                  <SelectItem value="walk_in">Walk-in Visit</SelectItem>
                  <SelectItem value="referral">Referral</SelectItem>
                  <SelectItem value="event">Event/Trade Show</SelectItem>
                  <SelectItem value="website">Website Inquiry</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <label className="text-sm font-medium mb-1 block">Notes</label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Initial notes about this lead..."
                className="rounded-xl"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} className="rounded-full">
              Cancel
            </Button>
            <Button type="submit" disabled={submitting} className="rounded-full">
              {submitting ? "Creating..." : "Create Lead"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Lead Detail Dialog Component
const LeadDetailDialog = ({ lead, open, onClose, onUpdate, headers }) => {
  const [status, setStatus] = useState(lead?.status || "cold");
  const [newNote, setNewNote] = useState("");
  const [followUpDate, setFollowUpDate] = useState("");
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    if (lead) {
      setStatus(lead.status);
    }
  }, [lead]);

  const handleStatusChange = async (newStatus) => {
    setUpdating(true);
    try {
      await axios.put(`${API}/sales-agent/leads/${lead.lead_id}`, { status: newStatus }, { headers });
      setStatus(newStatus);
      toast.success("Status updated");
    } catch (error) {
      toast.error("Failed to update status");
    } finally {
      setUpdating(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    
    setUpdating(true);
    try {
      await axios.post(`${API}/sales-agent/leads/${lead.lead_id}/note`, {
        note: newNote,
        follow_up_date: followUpDate || null
      }, { headers });
      toast.success("Note added");
      setNewNote("");
      setFollowUpDate("");
      onUpdate();
    } catch (error) {
      toast.error("Failed to add note");
    } finally {
      setUpdating(false);
    }
  };

  const getStatusColor = (s) => {
    const colors = {
      cold: "bg-blue-100 text-blue-700",
      contacted: "bg-yellow-100 text-yellow-700",
      warm: "bg-orange-100 text-orange-700",
      hot: "bg-red-100 text-red-700",
      converted: "bg-green-100 text-green-700",
      lost: "bg-gray-100 text-gray-700"
    };
    return colors[s] || "bg-gray-100 text-gray-700";
  };

  if (!lead) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <Building2 className="h-5 w-5 text-primary" />
            {lead.business_name}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Contact Info */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">Contact</p>
              <p className="font-medium">{lead.contact_name}</p>
            </div>
            {lead.phone && (
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">Phone</p>
                <p className="font-medium flex items-center gap-2">
                  <Phone className="h-4 w-4" />
                  {lead.phone}
                </p>
              </div>
            )}
            {lead.email && (
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">Email</p>
                <p className="font-medium flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  {lead.email}
                </p>
              </div>
            )}
            {lead.address && (
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">Address</p>
                <p className="font-medium flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  {lead.address}
                </p>
              </div>
            )}
          </div>

          {/* Status */}
          <div>
            <p className="text-sm text-muted-foreground mb-2">Status</p>
            <div className="flex flex-wrap gap-2">
              {["cold", "contacted", "warm", "hot", "converted", "lost"].map((s) => (
                <Button
                  key={s}
                  variant={status === s ? "default" : "outline"}
                  size="sm"
                  className={`rounded-full capitalize ${status === s ? "" : getStatusColor(s)}`}
                  onClick={() => handleStatusChange(s)}
                  disabled={updating}
                >
                  {s}
                </Button>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div>
            <p className="text-sm text-muted-foreground mb-2">Notes & Follow-ups</p>
            <div className="space-y-3 max-h-48 overflow-y-auto mb-4">
              {lead.notes?.length > 0 ? (
                lead.notes.map((note, idx) => (
                  <div key={idx} className="p-3 bg-muted/50 rounded-xl">
                    <p className="text-sm">{note.note}</p>
                    <div className="flex items-center justify-between mt-2">
                      <p className="text-xs text-muted-foreground">
                        {new Date(note.date).toLocaleDateString()}
                      </p>
                      {note.follow_up_date && (
                        <Badge variant="outline" className="text-xs">
                          <Calendar className="h-3 w-3 mr-1" />
                          Follow up: {new Date(note.follow_up_date).toLocaleDateString()}
                        </Badge>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">No notes yet</p>
              )}
            </div>

            {/* Add Note */}
            <div className="space-y-3 pt-3 border-t">
              <Textarea
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                placeholder="Add a note..."
                className="rounded-xl"
                rows={2}
              />
              <div className="flex items-center gap-3">
                <div className="flex-1">
                  <Input
                    type="date"
                    value={followUpDate}
                    onChange={(e) => setFollowUpDate(e.target.value)}
                    className="rounded-xl"
                    placeholder="Follow-up date"
                  />
                </div>
                <Button 
                  onClick={handleAddNote} 
                  disabled={!newNote.trim() || updating}
                  className="rounded-full"
                >
                  <MessageSquare className="h-4 w-4 mr-2" />
                  Add Note
                </Button>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SalesAgentDashboardPage;
